import * as pdfjsLib from 'pdfjs-dist';
import { GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const getPdfDocument = async (params: any) => {
    try {
        return await pdfjsLib.getDocument(params).promise;
    } catch (error: any) {
        if (error.message && error.message.includes('worker')) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
            return await pdfjsLib.getDocument(params).promise;
        }
        throw error;
    }
};

interface PdfMetadataInfo {
    Title?: string;
    Author?: string;
    Subject?: string;
    Creator?: string;
    Producer?: string;
    CreationDate?: string;
    ModDate?: string;
}

export interface ExtractedData {
    fullText: string;
    metadata: {
        totalPages: number;
        title?: string;
        author?: string;
        subject?: string;
        creator?: string;
        producer?: string;
        creationDate?: Date;
        modificationDate?: Date;
    };
    extractionDate: Date;
}

export interface PdfExtractionOptions {
    maxPages?: number;
    includeMetadata?: boolean;
}

export class PdfExtractor {
    private file: File;
    private options: Required<PdfExtractionOptions>;

    constructor(file: File, options: PdfExtractionOptions = {}) {
        this.file = file;
        this.options = {
            maxPages: options.maxPages || 10,
            includeMetadata: options.includeMetadata ?? true,
        };
    }

    async extractFullText(): Promise<string> {
        try {
            const arrayBuffer = await this.file.arrayBuffer();
            const pdf = await getPdfDocument({ data: arrayBuffer });

            let fullText = '';

            for (let pageNum = 1; pageNum <= Math.min(pdf.numPages, this.options.maxPages); pageNum++) {
                const page = await pdf.getPage(pageNum);
                const textContent = await page.getTextContent();

                const pageText = textContent.items
                    .map((item: any) => item.str)
                    .join(' ')
                    .replace(/\s+/g, ' ')
                    .trim();

                fullText += `\n--- Page ${pageNum} ---\n${pageText}\n`;
            }

            return fullText.trim();
        } catch (error) {
            throw new Error(`Failed to extract text from PDF: ${String(error)}`);
        }
    }

    async extractMetadata(): Promise<ExtractedData['metadata']> {
        try {
            const arrayBuffer = await this.file.arrayBuffer();
            const pdf = await getPdfDocument({ data: arrayBuffer });

            const metadata = await pdf.getMetadata();
            const info = metadata.info as PdfMetadataInfo;

            return {
                totalPages: pdf.numPages,
                title: info?.Title,
                author: info?.Author,
                subject: info?.Subject,
                creator: info?.Creator,
                producer: info?.Producer,
                creationDate: info?.CreationDate ? new Date(info.CreationDate) : undefined,
                modificationDate: info?.ModDate ? new Date(info.ModDate) : undefined,
            };
        } catch (error) {
            return {
                totalPages: 0,
            };
        }
    }

    async extractAll(): Promise<ExtractedData> {
        try {
            const [fullText, metadata] = await Promise.all([this.extractFullText(), this.options.includeMetadata ? this.extractMetadata() : Promise.resolve({ totalPages: 0 })]);

            return {
                fullText,
                metadata,
                extractionDate: new Date(),
            };
        } catch (error) {
            throw new Error(`Failed to extract PDF data: ${String(error)}`);
        }
    }
}
